import { useState, useEffect } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import {
    TECHNIQUE_OPTIONS,
    LOCATION_OPTIONS,
    WATER_TYPE_OPTIONS,
    CONTENT_TYPE_OPTIONS,
    LANGUAGE_OPTIONS,
    LEVEL_OPTIONS,
} from '../constants';

const inputClass =
    'w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors';
const labelClass = 'text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 block';

export interface CategorizationFormValues {
    modalities: string[];
    species: string[];
    location_types: string[];
    water_type: string;
    content_type: string;
    language_code: string;
    level: string;
}

interface AcademyVideoCategorizationFormProps {
    videoId: string;
    initialValues?: Partial<CategorizationFormValues>;
    onSave: (values: CategorizationFormValues) => Promise<void>;
    onCancel: () => void;
    isSaving?: boolean;
}

function parseJsonArray(val: unknown): string[] {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter((x): x is string => typeof x === 'string');
    if (typeof val === 'string') {
        try {
            const arr = JSON.parse(val);
            return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
        } catch {
            return [];
        }
    }
    return [];
}

export function AcademyVideoCategorizationForm({
    videoId,
    initialValues,
    onSave,
    onCancel,
    isSaving = false,
}: AcademyVideoCategorizationFormProps) {
    const [modalities, setModalities] = useState<string[]>(initialValues?.modalities ?? []);
    const [species, setSpecies] = useState<string[]>(initialValues?.species ?? []);
    const [locationTypes, setLocationTypes] = useState<string[]>(initialValues?.location_types ?? []);
    const [waterType, setWaterType] = useState(initialValues?.water_type ?? '');
    const [contentType, setContentType] = useState(initialValues?.content_type ?? '');
    const [languageCode, setLanguageCode] = useState(initialValues?.language_code ?? '');
    const [level, setLevel] = useState(initialValues?.level ?? '');
    const [speciesSearch, setSpeciesSearch] = useState('');
    const [speciesSearchOpen, setSpeciesSearchOpen] = useState(false);

    useEffect(() => {
        if (initialValues) {
            setModalities(initialValues.modalities ?? []);
            setSpecies(initialValues.species ?? []);
            setLocationTypes(initialValues.location_types ?? []);
            setWaterType(initialValues.water_type ?? '');
            setContentType(initialValues.content_type ?? '');
            setLanguageCode(initialValues.language_code ?? '');
            setLevel(initialValues.level ?? '');
        }
    }, [initialValues]);

    const { data: speciesResults = [], isLoading: speciesLoading } = useQuery({
        queryKey: ['species-catalog', speciesSearch],
        queryFn: async () => {
            if (!speciesSearch.trim() || speciesSearch.length < 2) return [];
            const { data, error } = await dbClient.rpc('get_species_catalog', {
                p_search_query: speciesSearch.trim(),
                p_limit: 15,
            });
            if (error) return [];
            return normalizeRpcResponse(data) as { scientific_name: string; common_names_es: string | null }[];
        },
        enabled: speciesSearch.trim().length >= 2,
        staleTime: 30_000,
    });

    const toggleModality = (value: string) => {
        setModalities((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    const toggleLocation = (value: string) => {
        setLocationTypes((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    const addSpecies = (scientificName: string) => {
        if (!species.includes(scientificName)) setSpecies((prev) => [...prev, scientificName]);
        setSpeciesSearch('');
        setSpeciesSearchOpen(false);
    };

    const removeSpecies = (scientificName: string) => {
        setSpecies((prev) => prev.filter((s) => s !== scientificName));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSave({
            modalities,
            species,
            location_types: locationTypes,
            water_type: waterType,
            content_type: contentType,
            language_code: languageCode,
            level,
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Categorización del vídeo</h3>

            <div>
                <label className={labelClass}>Modalidades</label>
                <p className="text-[11px] text-muted-foreground mb-2 leading-snug">
                    <strong>General</strong>: válido para todas las modalidades (nudos, licencia, etc.).
                    Puedes marcar varias si aplica (p. ej. jigging + jig casting para un vídeo de jigs).
                    Si el vídeo es de una sola técnica, usa solo esa — no confundas eging con jig casting/jigging.
                </p>
                <div className="flex flex-wrap gap-1.5">
                    {TECHNIQUE_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleModality(opt.value)}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                                modalities.includes(opt.value)
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <label className={labelClass}>Especies (nombre científico)</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {species.map((s) => (
                        <span
                            key={s}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-primary/15 text-primary"
                        >
                            {s}
                            <button
                                type="button"
                                onClick={() => removeSpecies(s)}
                                className="hover:bg-primary/20 rounded p-0.5"
                                aria-label="Quitar"
                            >
                                <X size={10} />
                            </button>
                        </span>
                    ))}
                </div>
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        value={speciesSearch}
                        onChange={(e) => {
                            setSpeciesSearch(e.target.value);
                            setSpeciesSearchOpen(true);
                        }}
                        onFocus={() => setSpeciesSearchOpen(true)}
                        placeholder="Buscar especie (mín. 2 caracteres)..."
                        className={inputClass}
                        style={{ paddingLeft: '2rem' }}
                    />
                    {speciesSearchOpen && speciesSearch.length >= 2 && (
                        <div className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-card shadow-lg max-h-48 overflow-y-auto">
                            {speciesLoading ? (
                                <div className="p-3 flex items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 size={14} className="animate-spin" />
                                    Buscando...
                                </div>
                            ) : speciesResults.length === 0 ? (
                                <div className="p-3 text-sm text-muted-foreground">
                                    Sin resultados
                                </div>
                            ) : (
                                speciesResults.map((sp) => (
                                    <button
                                        key={sp.scientific_name}
                                        type="button"
                                        onClick={() => addSpecies(sp.scientific_name)}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted/50 flex justify-between"
                                    >
                                        <span>{sp.scientific_name}</span>
                                        {sp.common_names_es && (
                                            <span className="text-muted-foreground text-xs">
                                                {sp.common_names_es}
                                            </span>
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div>
                <label className={labelClass}>Desde dónde</label>
                <div className="flex flex-wrap gap-1.5">
                    {LOCATION_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleLocation(opt.value)}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                                locationTypes.includes(opt.value)
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className={labelClass}>Tipo de agua</label>
                    <select
                        value={waterType}
                        onChange={(e) => setWaterType(e.target.value)}
                        className={inputClass}
                    >
                        {WATER_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Tipo de contenido</label>
                    <select
                        value={contentType}
                        onChange={(e) => setContentType(e.target.value)}
                        className={inputClass}
                    >
                        {CONTENT_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className={labelClass}>Idioma</label>
                    <select
                        value={languageCode}
                        onChange={(e) => setLanguageCode(e.target.value)}
                        className={inputClass}
                    >
                        {LANGUAGE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Nivel</label>
                    <select value={level} onChange={(e) => setLevel(e.target.value)} className={inputClass}>
                        {LEVEL_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex gap-2 pt-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted"
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                    {isSaving && <Loader2 size={14} className="animate-spin" />}
                    Guardar categorías
                </button>
            </div>
        </form>
    );
}
